import { BufferGeometry, Float32BufferAttribute } from "three";
import { SEGMENT_STRIDE } from "../tools/guided-navigation/guided-navigation-math";

/** Angle between the vertices of the circular arcs at the corridor turns and ends */
const ARC_STEP = Math.PI / 8;

/** Tolerance on the angle, to avoid an extra arc step due to rounding errors */
const ANGLE_EPSILON = 1e-6;

type Vec2 = [x: number, z: number];

type Segment = {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  /** Normalized direction */
  dx: number;
  dz: number;
};

/** Vertices and triangles of the geometry under construction */
type MeshData = {
  positions: number[];
  indices: number[];
};

/**
 * @returns the unit vectors obtained by rotating a unit vector by an angle, at most ARC_STEP apart
 * @param vector the vector to rotate
 * @param angle the total rotation
 */
function arc([x, z]: Vec2, angle: number): Vec2[] {
  const steps = Math.ceil(Math.abs(angle) / ARC_STEP - ANGLE_EPSILON);
  return Array.from({ length: steps + 1 }, (_, k) => {
    const phi = (angle * k) / steps;
    return [
      x * Math.cos(phi) - z * Math.sin(phi),
      x * Math.sin(phi) + z * Math.cos(phi),
    ];
  });
}

/**
 * Adds a vertex on the horizontal plane
 *
 * @param mesh the geometry under construction
 * @param x vertex x coordinate
 * @param z vertex z coordinate
 * @returns the index of the vertex
 */
function addVertex(mesh: MeshData, x: number, z: number): number {
  mesh.positions.push(x, 0, z);
  return mesh.positions.length / 3 - 1;
}

/**
 * Adds a circular sector
 *
 * @param mesh the geometry under construction
 * @param x sector center x coordinate
 * @param z sector center z coordinate
 * @param directions unit vectors from the center, where to place the arc vertices
 * @param radius sector radius
 */
function addFan(
  mesh: MeshData,
  x: number,
  z: number,
  directions: Vec2[],
  radius: number,
): void {
  const center = addVertex(mesh, x, z);
  let prev: number | undefined;
  for (const [vx, vz] of directions) {
    const next = addVertex(mesh, x + radius * vx, z + radius * vz);
    if (prev !== undefined) mesh.indices.push(center, prev, next);
    prev = next;
  }
}

/**
 * @returns the non-degenerate segments with their direction
 * @param segments the trajectory segments, see SEGMENT_STRIDE
 */
function toSegments(segments: Float32Array): Segment[] {
  const result: Segment[] = [];
  for (let o = 0; o < segments.length; o += SEGMENT_STRIDE) {
    const [x0, , z0, x1, , z1] = segments.subarray(o, o + SEGMENT_STRIDE);
    const length = Math.hypot(x1 - x0, z1 - z0);
    if (length > 0) {
      result.push({
        x0,
        z0,
        x1,
        z1,
        dx: (x1 - x0) / length,
        dz: (z1 - z0) / length,
      });
    }
  }
  return result;
}

/**
 * @returns a static flat mesh of the corridor around the trajectory segments, on the plane y = 0, ignoring their elevation.
 * Turns are rounded on the outer side and the trajectory ends are capped, with a vertex every PI / 8.
 * Overlapping parts of the corridor are not merged: the material is expected to draw each pixel once.
 * @param segments the trajectory segments, see SEGMENT_STRIDE. Consecutive segments sharing a point are joined.
 * @param radius corridor radius
 */
export function buildCorridorFloorGeometry(
  segments: Float32Array,
  radius: number,
): BufferGeometry {
  const mesh: MeshData = { positions: [], indices: [] };

  const segs = toSegments(segments);
  for (const [i, s] of segs.entries()) {
    const nx = -s.dz;
    const nz = s.dx;
    const a = addVertex(mesh, s.x0 + nx * radius, s.z0 + nz * radius);
    const b = addVertex(mesh, s.x0 - nx * radius, s.z0 - nz * radius);
    const c = addVertex(mesh, s.x1 + nx * radius, s.z1 + nz * radius);
    const d = addVertex(mesh, s.x1 - nx * radius, s.z1 - nz * radius);
    mesh.indices.push(a, b, c, b, d, c);

    const prev: Segment | undefined = i > 0 ? segs[i - 1] : undefined;
    const next = segs.at(i + 1);
    if (!prev || prev.x1 !== s.x0 || prev.z1 !== s.z0) {
      addFan(mesh, s.x0, s.z0, arc([nx, nz], Math.PI), radius);
    }
    if (!next || s.x1 !== next.x0 || s.z1 !== next.z0) {
      addFan(mesh, s.x1, s.z1, arc([nx, nz], -Math.PI), radius);
      continue;
    }
    // Only the outer side of a turn has a gap, the inner side is covered by the segments
    const turn = Math.atan2(
      s.dx * next.dz - s.dz * next.dx,
      s.dx * next.dx + s.dz * next.dz,
    );
    if (turn !== 0) {
      const side = -Math.sign(turn);
      addFan(mesh, s.x1, s.z1, arc([side * nx, side * nz], turn), radius);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(mesh.positions, 3),
  );
  geometry.setIndex(mesh.indices);
  return geometry;
}
